import {
  Box,
  Button,
  Flex,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react';
import Select from 'react-select';
import { OfficeHoursInput } from '../../../types/practices/types';
import {
  generateTimeOptions,
  timeToMinutes,
  to12h,
} from '../../../utils/timeConversion';

interface OfficeHoursInputProps {
  officeHoursInput: OfficeHoursInput[];
  setOfficeHoursInput: React.Dispatch<
    React.SetStateAction<OfficeHoursInput[]>
  >;
  setValue: (
    name: string,
    value:
      | string
      | boolean
      | string[]
      | OfficeHoursInput[]
      | undefined
  ) => void;
  to24h: (time12h: string) => string;
  lunchHours?: {
    start: string;
    end: string;
    startDisplay: string;
    endDisplay: string;
  } | null;
  observesLunchHours?: boolean;
}

export interface OfficeHoursInputRef {
  validate: () => string[];
}

interface TimeOption {
  label: string;
  value: string;
}

// Generate time options with 15-minute intervals only
const timeOptions: TimeOption[] = generateTimeOptions(0, 23, 15);

/**
 * Simple Time Dropdown - 15-minute intervals only
 */
interface TimeDropdownProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
}

const TimeDropdown: React.FC<TimeDropdownProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
}) => {
  const handleChange = (selectedOption: TimeOption | null) => {
    onChange(selectedOption ? selectedOption.value : '');
  };

  const currentOption = timeOptions.find(
    (option) => option.value === value
  );

  return (
    <Box minW="120px">
      <Select
        options={timeOptions}
        value={currentOption}
        onChange={handleChange}
        placeholder={placeholder}
        isClearable
        isSearchable
        isDisabled={disabled}
        styles={{
          control: (provided) => ({
            ...provided,
            minHeight: '32px',
            fontSize: '0.875rem',
            border: '1px solid #e2e8f0',
            borderRadius: '0.375rem',
            backgroundColor: disabled ? '#f7fafc' : 'white',
          }),
          option: (provided, state) => ({
            ...provided,
            fontSize: '0.875rem',
            backgroundColor: state.isSelected
              ? '#3182ce'
              : state.isFocused
              ? '#e6f3ff'
              : 'white',
            color: state.isSelected ? 'white' : '#2d3748',
            padding: '0.5rem 0.75rem',
          }),
        }}
      />
    </Box>
  );
};

const OfficeHoursInputComponent = forwardRef<
  OfficeHoursInputRef,
  OfficeHoursInputProps
>(
  (
    {
      officeHoursInput,
      setOfficeHoursInput,
      setValue,
      to24h,
      lunchHours,
      observesLunchHours,
    },
    ref
  ) => {
    const toast = useToast();

    // Simple lunch validation for UX feedback
    const getLunchValidationMessages = useCallback(() => {
      if (
        !observesLunchHours ||
        !lunchHours?.start ||
        !lunchHours?.end
      ) {
        return [];
      }

      const lunchStartMinutes = timeToMinutes(lunchHours.start);
      const lunchEndMinutes = timeToMinutes(lunchHours.end);
      const messages: Array<{
        day: string;
        message: string;
        applied: boolean;
      }> = [];

      officeHoursInput.forEach((day) => {
        if (day.closed || !day.open || !day.close) {
          return; // Skip closed days
        }

        const dayOpenMinutes = timeToMinutes(day.open);
        const dayCloseMinutes = timeToMinutes(day.close);

        // Check if lobby closure is outside office hours
        // Reject if lobby closure starts after office closes OR ends before office opens OR extends beyond office hours
        const lobbyClosureOutsideOfficeHours =
          lunchStartMinutes >= dayCloseMinutes ||
          lunchEndMinutes <= dayOpenMinutes ||
          lunchEndMinutes > dayCloseMinutes;

        if (lobbyClosureOutsideOfficeHours) {
          let reason = 'completely outside office hours';
          if (lunchStartMinutes >= dayCloseMinutes) {
            reason = `starts after office closes at ${to12h(
              day.close
            )}`;
          } else if (lunchEndMinutes <= dayOpenMinutes) {
            reason = `ends before office opens at ${to12h(day.open)}`;
          } else if (lunchEndMinutes > dayCloseMinutes) {
            reason = `ends after office closes at ${to12h(day.close)}`;
          }

          messages.push({
            day: day.day,
            message: `Lobby closure ${lunchHours.startDisplay}–${lunchHours.endDisplay} cannot be shown on ${day.day} (${reason})`,
            applied: false,
          });
        }
      });

      return messages;
    }, [observesLunchHours, lunchHours, officeHoursInput]);

    const lunchValidationMessages = getLunchValidationMessages();

    // Copy day functionality
    const handleCopyDayToAll = useCallback(
      (sourceDayIndex: number) => {
        const sourceDay = officeHoursInput[sourceDayIndex];
        if (!sourceDay) return;

        const updatedHours = officeHoursInput.map((day, index) => {
          if (index === sourceDayIndex) return day; // Keep source day unchanged

          // Only apply hours to days that are already open - skip closed days
          if (day.closed) return day;

          return {
            ...day,
            open: sourceDay.open,
            close: sourceDay.close,
            // Don't change the closed status - keep existing open/closed state
            openDisplay: sourceDay.openDisplay,
            closeDisplay: sourceDay.closeDisplay,
            openError: false,
            closeError: false,
          };
        });

        setOfficeHoursInput(updatedHours);
        setValue('officeHoursInput', updatedHours);

        // Count affected days - only open days that actually received new hours
        const dayNames = [
          'Sun',
          'Mon',
          'Tue',
          'Wed',
          'Thu',
          'Fri',
          'Sat',
        ];
        const affectedDays = officeHoursInput
          .map((day, index) => ({
            day,
            index,
            name: dayNames[index],
          }))
          .filter(
            ({ day, index }) =>
              index !== sourceDayIndex &&
              !day.closed && // Only count open days
              (day.open !== sourceDay.open ||
                day.close !== sourceDay.close)
          )
          .map(({ name }) => name);

        // Count skipped closed days for better user feedback
        const skippedClosedDays = officeHoursInput
          .map((day, index) => ({
            day,
            index,
            name: dayNames[index],
          }))
          .filter(
            ({ day, index }) => index !== sourceDayIndex && day.closed
          )
          .map(({ name }) => name);

        if (affectedDays.length > 0) {
          let description = `Copied ${
            sourceDay.day
          } hours to ${affectedDays.join(', ')}`;
          if (skippedClosedDays.length > 0) {
            description += `. Skipped closed days: ${skippedClosedDays.join(
              ', '
            )}`;
          }

          toast({
            title: 'Hours Copied',
            description,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else if (skippedClosedDays.length > 0) {
          toast({
            title: 'No Changes',
            description: `No open days to copy to. Closed days (${skippedClosedDays.join(
              ', '
            )}) were skipped.`,
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'No Changes',
            description: 'All open days already have the same hours',
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
        }
      },
      [officeHoursInput, setOfficeHoursInput, setValue, toast]
    );

    const toggleDayClosedStatus = (index: number) => {
      const next = officeHoursInput.map((item, i) =>
        i === index
          ? {
              ...item,
              closed: !item.closed,
              openError: false,
              closeError: false,
            }
          : item
      );

      setOfficeHoursInput(next);
      setValue('officeHoursInput', next);
    };

    const handleTimeChange = (
      index: number,
      field: 'open' | 'close',
      value: string
    ) => {
      const next = officeHoursInput.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: to24h(value) || '',
              [`${field}Display`]: value,
              [`${field}Error`]: false,
            }
          : item
      );

      setOfficeHoursInput(next);
      setValue('officeHoursInput', next);
    };

    // Validation function
    const validateOfficeHours = useCallback((): string[] => {
      const errors: string[] = [];

      officeHoursInput.forEach((day) => {
        if (!day.closed) {
          if (!day.open || !day.close) {
            errors.push(
              `${day.day}: Both open and close times are required`
            );
          } else if (day.open >= day.close) {
            errors.push(
              `${day.day}: Open time must be before close time`
            );
          }
        }
      });

      return errors;
    }, [officeHoursInput]);

    // Expose validation function to parent component
    useImperativeHandle(
      ref,
      () => ({
        validate: validateOfficeHours,
      }),
      [validateOfficeHours]
    );

    return (
      <Flex direction="column" gap="0.5rem">
        {officeHoursInput.map((day, index) => {
          // Check if this day has lunch validation issues
          const dayLunchIssue = lunchValidationMessages.find(
            (msg) => msg.day === day.day
          );
          const hasLunchWarning = Boolean(dayLunchIssue);

          return (
            <Flex
              key={day.day}
              alignItems="center"
              gap="1rem"
              py="0.5rem"
              px="0.75rem"
              borderRadius="0.25rem"
              bg={hasLunchWarning ? 'orange.50' : 'white'}
              border="1px solid"
              borderColor={
                hasLunchWarning ? 'orange.200' : 'gray.200'
              }
              minH="2.5rem"
              flexWrap="wrap"
              sx={{
                '&:hover': {
                  bg: hasLunchWarning ? 'orange.100' : 'gray.50',
                  borderColor: hasLunchWarning
                    ? 'orange.300'
                    : 'gray.300',
                },
              }}
            >
              {/* Toggle Switch */}
              <Flex flexShrink={0} alignItems="center" w="4rem">
                <Switch
                  isChecked={!day.closed}
                  onChange={() => toggleDayClosedStatus(index)}
                  size="sm"
                  colorScheme="blue"
                />
              </Flex>

              {/* Day Label */}
              <Text
                fontSize="0.875rem"
                fontWeight="500"
                color="gray.700"
                w="5rem"
                flexShrink={0}
              >
                {day.day}
              </Text>

              {/* Time Inputs and Controls */}
              <Flex
                direction="column"
                gap="0.5rem"
                flex="1"
                minW="280px"
              >
                {/* Time Inputs Row */}
                <Flex
                  alignItems="center"
                  gap="0.5rem"
                  flexWrap="wrap"
                >
                  <TimeDropdown
                    value={day.openDisplay || ''}
                    onChange={(value) =>
                      handleTimeChange(index, 'open', value)
                    }
                    placeholder="Open time"
                    disabled={day.closed}
                  />

                  <Text fontSize="0.875rem" color="gray.500" mx="2">
                    -
                  </Text>

                  <TimeDropdown
                    value={day.closeDisplay || ''}
                    onChange={(value) =>
                      handleTimeChange(index, 'close', value)
                    }
                    placeholder="Close time"
                    disabled={day.closed}
                  />

                  {/* Copy to All Days Button */}
                  {!day.closed && (day.open || day.close) && (
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => handleCopyDayToAll(index)}
                      fontSize="0.75rem"
                      px="2"
                      h="6"
                      minW="auto"
                      _hover={{
                        bg: 'blue.50',
                        color: 'blue.600',
                      }}
                    >
                      Copy to all
                    </Button>
                  )}
                </Flex>

                {/* Lunch Warning Message - Full Width on Separate Line */}
                {hasLunchWarning && dayLunchIssue && (
                  <Flex alignItems="center" gap="0.5rem" width="100%">
                    <Text
                      fontSize="0.75rem"
                      color="orange.600"
                      fontWeight="500"
                      lineHeight="1.3"
                    >
                      ⚠️ {dayLunchIssue.message}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    );
  }
);

OfficeHoursInputComponent.displayName = 'OfficeHoursInputComponent';

export default OfficeHoursInputComponent;
